import { StatusBadge } from "@rosc/ui/v32/status";
import { Pause } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import type { LiveTranscriptGap } from "@/store/transcriptionStreaming.store";

/** 0:45, 12:03, 1:02:09 */
export const gapClock = (seconds: number): string => {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
};

/**
 * Where the live transcript missed part of the recording (a locked screen,
 * a frozen page, a network drop), in the transcript at that point: "Adding
 * 0:45–1:42…" while that audio is sent again, or "Paused 0:45–1:42 — this
 * part was recorded and will be in the note" when it was too long to send.
 */
export const LiveTranscriptGapLine = ({
  gap,
}: {
  gap: Pick<LiveTranscriptGap, "from" | "to" | "status">;
}): JSX.Element => {
  const { t } = useTranslation();
  const range = { from: gapClock(gap.from), to: gapClock(gap.to) };
  if (gap.status === "adding")
    return (
      <li data-testid="live-gap-adding" role="status">
        <StatusBadge status="processing">
          {t("components.recorderInner.liveGapAdding", range)}
        </StatusBadge>
      </li>
    );
  return (
    <li data-testid="live-gap-paused">
      <p className="flex items-start gap-2 rounded-xl border border-dashed border-v3-border-strong px-3 py-2 text-[13.5px] leading-5 text-v3-text-2">
        <Pause
          aria-hidden
          className="mt-0.5 size-3.5 shrink-0 fill-current text-v3-text-3"
        />
        <span>{t("components.recorderInner.liveGapPaused", range)}</span>
      </p>
    </li>
  );
};
